import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ChartBarIcon,
  HomeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  InboxIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  CommandLineIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { COMMUNITY_CHAT_ENABLED, QUANT_AGI_ENABLED } from '../config/features';

const Navigation: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  if (!isAuthenticated) {
    return null;
  }

  const displayName =
    (user?.username && String(user.username).trim()) ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    (user?.email ? user.email.split('@')[0] : 'Account');

  /** Primary app sections — may truncate on narrow desktops. */
  const mainNav = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Deploy Strategies', href: '/deploy-strategies', icon: RocketLaunchIcon },
    ...(QUANT_AGI_ENABLED ? [{ name: 'Quant AGI', href: '/quant-agi', icon: CpuChipIcon }] : []),
    { name: 'Stock Charts', href: '/charts', icon: ChartBarIcon },
    { name: 'Signals', href: '/opportunity-signals', icon: InboxIcon },
    ...(COMMUNITY_CHAT_ENABLED
      ? [{ name: 'Chat', href: '/chat', icon: ChatBubbleLeftRightIcon }]
      : [])
  ];

  /** Always visible next to account controls (MCP immediately left of Profile). Deploy 2026-09-28. */
  const accountNav = [
    { name: 'MCP', href: '/mcp', icon: CommandLineIcon },
    { name: 'Profile', href: '/profile', icon: UserCircleIcon }
  ];

  const mobileNav = [...mainNav, ...accountNav];

  const isActive = (href: string) =>
    location.pathname === href ||
    (href === '/dashboard' && location.pathname === '/ai-agent') ||
    (href === '/mcp' && location.pathname.startsWith('/mcp')) ||
    (href === '/deploy-strategies' && location.pathname.startsWith('/deploy-strategies'));

  const linkClass = (href: string) =>
    `flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive(href)
        ? 'bg-white/[0.08] text-kib-fg'
        : 'text-kib-muted hover:bg-white/[0.06] hover:text-kib-fg'
    }`;

  return (
    <nav data-build="20261010m" className="sticky top-0 z-50 nav-shell pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex h-14 max-w-[1360px] items-center justify-between gap-2 px-4 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <Link
            to="/dashboard"
            className="shrink-0 text-[15px] font-semibold tracking-tight text-kib-fg hover:text-white"
            onClick={() => setMobileOpen(false)}
          >
            KeepItBased
          </Link>

          <div className="hidden min-w-0 lg:flex lg:items-center lg:gap-0.5 lg:overflow-x-auto">
            {mainNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.name} to={item.href} className={linkClass(item.href)}>
                  <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <div className="hidden sm:flex sm:items-center sm:gap-0.5">
            {accountNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.name} to={item.href} className={linkClass(item.href)}>
                  <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  {item.name}
                </Link>
              );
            })}
          </div>
          <span className="hidden truncate text-sm text-kib-muted xl:inline max-w-[160px]" title={user?.email}>
            {displayName}
          </span>
          <button
            type="button"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-2 text-kib-muted hover:bg-white/[0.06] hover:text-kib-fg lg:hidden"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
          <button
            type="button"
            onClick={logout}
            className="hidden sm:flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-kib-muted hover:bg-white/[0.06] hover:text-kib-fg"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            <span className="hidden md:inline">Log out</span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-nav-menu"
          className="max-h-[min(70vh,calc(100dvh-3.5rem-env(safe-area-inset-top)))] overflow-y-auto overscroll-contain border-t border-white/[0.06] lg:hidden"
        >
          <div className="mx-auto max-w-[1360px] space-y-0.5 px-3 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
            {mobileNav.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${linkClass(item.href)} min-h-[44px]`}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
                  {item.name}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                logout();
              }}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-kib-muted hover:bg-white/[0.06] hover:text-kib-fg sm:hidden"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              Log out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
